import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import { Article, Coins, EyeSlash, MagicWand, PencilSimple } from "@phosphor-icons/react";
import { TOKEN_COSTS } from "@stylingcv/config";
import { defaultSections } from "@stylingcv/schema";
import { RichInput } from "@stylingcv/ui";
import { useEffect, useRef } from "react";

import { AiActions } from "@/client/components/ai-actions";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";
import { trackAIEvent } from "@/client/utils/gtm";

import { SectionOptions } from "./shared/section-options";

export const SummarySection = () => {
  const setValue = useResumeStore((state) => state.setValue);
  const { open } = useDialog("generate-summary");
  const isUserTypingRef = useRef<boolean>(false);

  const section = useResumeStore((state) => {
    try { return state.resume.data.sections.summary || defaultSections.summary; }
    catch { return defaultSections.summary; }
  });

  const basics = useResumeStore((state) => {
    try { return state.resume.data.basics; }
    catch { return null; }
  });

  const updateSummaryContent = (value: string) => {
    try {
      isUserTypingRef.current = true;
      setValue("sections.summary.content", value);
    } catch { /* silent */ }
  };

  const handleGenerateSummary = () => {
    trackAIEvent.summaryGenerated();
    open("create", { id: createId(), item: { jobTitle: basics?.headline || "" } });
  };

  return (
    <div id="summary" className="flex h-full flex-col overflow-y-auto">
      {/* Header — matches SectionBase exactly */}
      <header className="flex items-center gap-3 border-b border-[var(--border,#e5e5e5)] px-6 py-5">
        {/* Section icon box */}
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gray-100">
          <Article size={18} className="text-[var(--fg-3,#737373)]" />
        </div>

        {/* Title */}
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-2xl font-bold leading-tight text-[var(--fg-1,#171717)]">
            {section.name}
          </h2>
          {!section.visible && (
            <div className="mt-0.5 flex items-center gap-1 text-[var(--fg-4,#a3a3a3)]">
              <EyeSlash size={12} />
              <span className="text-[10px] font-medium uppercase tracking-widest">{t`Hidden`}</span>
            </div>
          )}
        </div>

        {/* AI Write button */}
        <button
          type="button"
          onClick={handleGenerateSummary}
          className="flex shrink-0 items-center gap-1.5 rounded-full border border-[#268599]/40 bg-[#268599] px-3 py-1.5 text-white shadow-[0_0_12px_rgba(38,133,153,0.30)] transition-all hover:bg-[#155469] hover:shadow-[0_0_18px_rgba(38,133,153,0.45)]"
        >
          <MagicWand size={13} weight="fill" />
          <span className="text-[10px] font-bold uppercase tracking-wider">{t`AI Write`}</span>
          <span className="ms-0.5 flex items-center gap-0.5 rounded-full bg-black/20 px-1.5 py-0.5">
            <Coins size={10} className="text-yellow-300" />
            <span className="text-[10px] font-bold leading-none">{TOKEN_COSTS.AI_SUMMARY}</span>
          </span>
        </button>

        {/* Pencil rename — stub, summary name rarely changed but keep parity */}
        <button
          className="shrink-0 rounded p-1.5 text-gray-400 transition-colors hover:text-gray-600"
          title={t`Rename section`}
          onClick={() => {/* rename handled inline in SectionBase; summary keeps it simple */}}
        >
          <PencilSimple size={16} />
        </button>

        {/* Section options (⋯) */}
        <div onClick={(e) => e.stopPropagation()}>
          <SectionOptions id="summary" />
        </div>
      </header>

      {/* Editor */}
      <main className="flex-1 p-5">
        <div className={section.visible ? undefined : "opacity-50 pointer-events-none"}>
          <RichInput
            content={section.content || ""}
            footer={(editor) => (
              <AiActions
                value={editor.getText()}
                onChange={(value) => {
                  editor.commands.setContent(value, true);
                  updateSummaryContent(value);
                }}
              />
            )}
            onChange={updateSummaryContent}
          />
        </div>
      </main>
    </div>
  );
};
